// Подписи и словари раздела сертификатов РМРС/РКО. Только показ.
//
// ЧТО ЗДЕСЬ ЛЕЖИТ И ПОЧЕМУ
// Картотека сертификатов — это 244 PDF из папки Nextcloud, разобранные языковой моделью.
// Коды неполноты (problems) приходят из ядра как есть («модель_из_имени_файла»), и человеку
// такое показывать нельзя: это код для нас, а не объяснение для него. Здесь короткие
// человеческие подписи и — главное — что из этого СЛЕДУЕТ для того, кто ищет сертификат.
// Неизвестный код НЕ ПРОПАДАЕТ: он показывается как есть (см. подписьПроблемы). Если перенос
// заведёт новый код, экран его покажет, а не промолчит.
//
// ЧЕГО В ДАННЫХ НЕТ — тоже здесь, потому что об этом надо говорить на экране:
// СРОКА ДЕЙСТВИЯ. В индексе портала его нет ни у одного файла. Значит раздел отвечает
// «такой сертификат есть», а не «он действует», и колонки «действует до» здесь нет вовсе —
// пустая колонка со сроком читалась бы как «бессрочно». Тексты оговорок приходят из ядра
// (/certs/refs → data_notes): наполнится источник — и раздел это увидит сам.

// ─────────── адреса раздела ───────────
// Ровно эти пути нужно добавить маршрутами в App.tsx.
// Карточка — НЕ сегментом пути: ключ файла в R2 это путь с косыми чертами, кириллицей,
// пробелами (иногда двойными) и точками («CERTS_RKO/РМРС/2026/6.5.31 26.44.01.01265.269.pdf»).
// В сегменте пути такое либо ломается на слешах, либо превращается в простыню %-кодов.
export const ПУТИ = {
  список: '/certs',
  сертификат: (ключ: string) => `/certs?key=${encodeURIComponent(ключ)}`,
} as const

export const РАЗМЕР_СТРАНИЦЫ = 50 // как PAGE в certs_api.py

// ─────────── коды неполноты ───────────

interface ПодписьПроблемы {
  ярлык: string
  /** Что это значит для того, кто ищет сертификат. Не пересказ кода, а следствие. */
  смысл: string
  /** true — строке нельзя верить как подтверждению конкретного прибора. */
  важно: boolean
}

const ПРОБЛЕМЫ: Record<string, ПодписьПроблемы> = {
  нет_серийников: {
    ярлык: 'без заводских номеров',
    смысл: 'в свидетельстве нет заводских номеров — либо это одобрение типа на всю модель,'
      + ' либо номера не удалось прочитать. Конкретный прибор такой файл не подтверждает.',
    важно: true,
  },
  модель_из_имени_файла: {
    ярлык: 'модель не прочитана',
    смысл: 'модель из PDF прочитать не удалось, в поле стоит имя файла. Искать такой файл'
      + ' надо по имени, а что за прибор — смотреть в самом PDF.',
    важно: true,
  },
  вид_не_определён: {
    ярлык: 'вид не определён',
    смысл: 'РМРС это или РКО, из PDF не прочиталось. Смотрите документ.',
    важно: false,
  },
  нет_года: {
    ярлык: 'без года',
    смысл: 'года документа в PDF не нашлось. Это не «свежий» и не «старый» — просто неизвестно.',
    важно: false,
  },
  дубли_серийников: {
    ярлык: 'номер повторяется',
    смысл: 'один и тот же номер прочитан дважды. На поиск не влияет, но вычитка неаккуратна.',
    важно: false,
  },
  вид_нестандартный: {
    ярлык: 'не РМРС и не РКО',
    смысл: 'это не свидетельство Регистра и не РКО (например, RoHS или паспорт безопасности).',
    важно: false,
  },
  размер_неизвестен: {
    ярлык: 'размер неизвестен',
    смысл: 'файла нет в кэше синка портала — размер показать нечем.',
    важно: false,
  },
}

/** Подпись кода неполноты. Неизвестный код показываем как есть, а не прячем. */
export function подписьПроблемы(код: string): ПодписьПроблемы {
  return ПРОБЛЕМЫ[код] ?? { ярлык: код, смысл: 'новый код неполноты — см. import_certs.py', важно: false }
}

/** Есть ли среди кодов такой, из-за которого строке нельзя верить как подтверждению. */
export const серьёзнаяНеполнота = (коды?: string[] | null): boolean =>
  (коды ?? []).some((к) => подписьПроблемы(к).важно)

// ─────────── вид свидетельства ───────────
// В данных пять значений: РМРС (205), РКО (33), пусто (3), иное (2), RoHS (1). Свой словарь
// расшифровок не нужен — значения и так короткие; нужен только показ пустого, потому что
// прочерк на месте вида читается как «обычный сертификат».

export function видСвидетельства(v?: string | null): { ярлык: string; известен: boolean } {
  const s = (v || '').trim()
  if (!s) return { ярлык: 'вид не определён', известен: false }
  return { ярлык: s, известен: s === 'РМРС' || s === 'РКО' }
}

/** Класс значка для вида. Голый .badge — ярлык, а не оценка: оценивать тут нечего. */
export const классВида = (v?: string | null): string =>
  видСвидетельства(v).известен ? 'badge' : 'badge work'

// ─────────── размер файла ───────────

export function размерФайла(байт?: number | null): string {
  if (байт === null || байт === undefined) return 'размер неизвестен'
  if (байт < 1024) return `${байт} Б`
  if (байт < 1024 * 1024) return `${Math.round(байт / 1024)} КБ`
  return `${(байт / (1024 * 1024)).toFixed(1)} МБ`
}

// ─────────── строка запроса ───────────

export interface ФильтрыСертов {
  model?: string
  serial?: string
  deep?: boolean
  registr?: string
  year?: string
  folder?: string
  problem?: string
  has_serials?: string
  sort?: string
  start?: number
  limit?: number
}

/** Собирает ?a=b&… Пустые значения не отправляем: ядро на пустой фильтр не смотрит,
 *  но лишний параметр в queryKey ломал бы кеш TanStack Query на каждом нажатии клавиши. */
export function строкаЗапроса(ф: ФильтрыСертов): string {
  const п = new URLSearchParams()
  if (ф.model) п.set('model', ф.model)
  if (ф.serial) п.set('serial', ф.serial)
  if (ф.deep) п.set('deep', '1')
  if (ф.registr) п.set('registr', ф.registr)
  if (ф.year) п.set('year', ф.year)
  if (ф.folder) п.set('folder', ф.folder)
  if (ф.problem) п.set('problem', ф.problem)
  if (ф.has_serials) п.set('has_serials', ф.has_serials)
  if (ф.sort) п.set('sort', ф.sort)
  if (ф.start) п.set('start', String(ф.start))
  if (ф.limit) п.set('limit', String(ф.limit))
  const s = п.toString()
  return s ? `?${s}` : ''
}

// ─────────── подписи режимов поиска ───────────
// Режим приходит из ядра (search_model / search_serial). Экран ОБЯЗАН его показать: пустой
// ответ на «18» в режиме «с начала строки» — это не «сертификата нет», это «ищите иначе».

export const ПОЯСНЕНИЕ_РЕЖИМА: Record<string, string> = {
  'с начала строки': 'искали с начала названия модели и имени файла — по индексу, быстро',
  'внутри строки': 'искали внутри строки — полным проходом по картотеке',
  'точное совпадение': 'номер совпал целиком',
  'внутри номера': 'искали часть номера внутри заводских номеров',
  нет: '',
}
